import type { SahiConfig } from "@/core/cameraApi/schemas/nn";
import { useEffect, useImperativeHandle, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { CameraPreview } from "./CameraPreview";
import { ConfigurationSidebar } from "./ConfigurationSidebar";
import { useConfigurationState } from "./useConfigurationState";

export interface ConfigurationTabHandle {
  saveIfDirty: () => Promise<{ capturedVideoId: number | null }>;
}

interface ConfigurationTabProps {
  projectId: number;
  configId: number | null;
  sahiConfig: SahiConfig | null;
  onSahiConfigChange: (config: SahiConfig | null) => void;
  /**
   * The model picker's value, saved or not; undefined once the form unmounts
   * and the saved configuration is the only truth again.
   */
  onModelSelectionChange?: (modelId: string | null | undefined) => void;
  ref?: Ref<ConfigurationTabHandle>;
}

export const ConfigurationTab = ({
  projectId,
  configId,
  sahiConfig,
  onSahiConfigChange,
  onModelSelectionChange,
  ref,
}: ConfigurationTabProps) => {
  const { t } = useTranslation(["run", "common"]);
  if (!configId) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-sm text-muted-foreground">
          {t("configuration.noConfiguration")}
        </p>
      </div>
    );
  }

  return (
    <ConfigurationTabContent
      ref={ref}
      projectId={projectId}
      configId={configId}
      sahiConfig={sahiConfig}
      onSahiConfigChange={onSahiConfigChange}
      onModelSelectionChange={onModelSelectionChange}
    />
  );
};

const ConfigurationTabContent = ({
  projectId,
  configId,
  sahiConfig,
  onSahiConfigChange,
  onModelSelectionChange,
  ref,
}: {
  projectId: number;
  configId: number;
  sahiConfig: SahiConfig | null;
  onSahiConfigChange: (config: SahiConfig | null) => void;
  onModelSelectionChange?: (modelId: string | null | undefined) => void;
  ref?: Ref<ConfigurationTabHandle>;
}) => {
  const { t } = useTranslation(["run", "common"]);
  const {
    streams,
    trainedModels,
    capturedVideos,
    selectedCamera,
    selectedStream,
    setSelectedStream,
    selectedModelId,
    setSelectedModelId,
    selectedVideoId,
    setSelectedVideoId,
    zoneConfig,
    setZoneConfig,
    hasChanges,
    handleSave,
  } = useConfigurationState(projectId, configId);

  useImperativeHandle(
    ref,
    () => ({
      saveIfDirty: async () => {
        if (hasChanges) return await handleSave();
        return { capturedVideoId: selectedVideoId };
      },
    }),
    [hasChanges, handleSave, selectedVideoId],
  );

  // The Deploy button sits in the subheader, outside this form, and has to
  // grey out the moment the model is cleared here, not after a save.
  useEffect(() => {
    onModelSelectionChange?.(selectedModelId);
    return () => onModelSelectionChange?.(undefined);
  }, [selectedModelId, onModelSelectionChange]);

  return (
    <div className="flex h-full min-h-0 flex-1 overflow-hidden bg-background">
      <ConfigurationSidebar
        trainedModels={trainedModels}
        selectedModelId={selectedModelId}
        onModelChange={setSelectedModelId}
        onModelClear={() => setSelectedModelId(null)}
        zoneConfig={zoneConfig}
        onZoneConfigChange={setZoneConfig}
        streams={streams}
        selectedCamera={selectedCamera}
        selectedStream={selectedStream}
        onStreamChange={setSelectedStream}
        capturedVideos={capturedVideos}
        selectedVideoId={selectedVideoId}
        onVideoChange={setSelectedVideoId}
        sahiConfig={sahiConfig}
        onSahiConfigChange={onSahiConfigChange}
      />

      <main className="flex min-w-0 flex-1 flex-col gap-4 overflow-y-auto p-6">
        <p className="text-xl">{t("configuration.liveStream")}</p>
        <CameraPreview
          zoneConfig={zoneConfig}
          selectedCamera={selectedCamera}
          selectedStream={selectedStream}
        />
      </main>
    </div>
  );
};
